/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The closed vocabularies and bounds of the laadpalen catalog — what a
 * charge-point model's specs and context, and a manufacturer record, may say.
 *
 * ONE list per field. Plain ESM JavaScript with zero imports (the
 * staticRoutes.js pattern) because its consumers cannot share TypeScript: the
 * research verifier and the seed generator under scripts/, the tsc server's
 * sanitizers, the bake, and the admin's selects and CSV columns. A value that
 * is not in its list here is not a value: the verifier drops it from research,
 * the server drops it from a save, and no surface has a label for it.
 *
 * Each list is cast `@type {const}` so TypeScript reads it as a tuple of
 * literals: the value types in src/lib/midChargers.ts (`ChargerPhases`, …) are
 * derived from these lists, and without the cast every one of them widens to
 * `string`.
 */

/** Mains phases the model charges on: one, three, or both (configurable or sold as both). */
export const CHARGER_PHASES = Object.freeze(/** @type {const} */ (['1', '3', '1/3']));

/** How the car connects: a type-2 socket, a fixed cable, or sold in both versions. */
export const CHARGER_CONNECTORS = Object.freeze(/** @type {const} */ (['socket', 'cable', 'both']));

/** Data links on the unit. `cellular` is any SIM-based link (2G/4G/LTE-M/NB-IoT). */
export const CHARGER_CONNECTIVITY = Object.freeze(
  /** @type {const} */ (['wifi', 'ethernet', 'cellular', 'bluetooth'])
);

/** OCPP versions the manufacturer documents. An empty list on a model = "no OCPP", stated. */
export const CHARGER_OCPP_VERSIONS = Object.freeze(
  /** @type {const} */ (['1.5', '1.6', '2.0.1', '2.1'])
);

/** Load balancing: dynamic (follows the house connection), static (a fixed cap) or none. */
export const CHARGER_LOAD_BALANCING = Object.freeze(
  /** @type {const} */ (['dynamic', 'static', 'none'])
);

/** Who the manufacturer positions the model for. */
export const CHARGER_AUDIENCES = Object.freeze(
  /** @type {const} */ (['home', 'business', 'public'])
);

/** On the manufacturer's current range, or discontinued by its own account. */
export const CHARGER_AVAILABILITY = Object.freeze(
  /** @type {const} */ (['current', 'discontinued'])
);

/** Numeric and text bounds, shared by every validator. */
export const CHARGER_LIMITS = Object.freeze({
  powerKwMin: 1,
  powerKwMax: 400,
  launchYearMin: 2005,
  launchYearMax: 2027,
  foundedMin: 1800,
  foundedMax: 2027,
  warrantyYearsMax: 15,
  // Text lengths (characters).
  noteMax: 600, // the register note — the limit server/routes/midChargers.ts always had
  researchNoteMax: 400, // a note the research writes for a row that had none
  successorMax: 120,
  companyMax: 120,
  cityMax: 80,
  parentMax: 120,
  descriptionMax: 500,
  backofficeMax: 300,
  warrantyNoteMax: 200,
  appNameMax: 120,
  phoneMax: 40,
  emailMax: 120,
  urlMax: 500,
  sourceTitleMax: 160,
  sourcesMaxPerModel: 3,
  sourcesMaxPerBrand: 5,
  seoTitleMax: 120,
  seoDescriptionMax: 320,
});

/** Evidence quotes: a verbatim excerpt of a saved source, this long. */
export const CHARGER_EVIDENCE_QUOTE = Object.freeze({ min: 15, max: 300 });
